import { createElement } from 'react';

import { MenuProps } from 'antd';
import { ItemType } from 'antd/es/menu/interface';
import { layoutLib } from 'layout/main-layout';
import { ProductCategory } from 'models/store';
import { User } from 'models/user';
import { usersApi } from 'services/rtk/users';

import { renderNavLink } from '../ui';

export interface OutletContext extends User {}

interface Return {
  items: ItemType[];
  selectedKeys: string[];
  openKeys: string[];
}

export const useMenu = (currentLocation: string, user?: User, products: ProductCategory[] = []): Return => {
  const { data: me } = usersApi.useFetchMeQuery();

  if (!user?.role) return { items: [], selectedKeys: [], openKeys: [] };

  const isActivePath = (itemPath: string, locationPath: string) => {
    const itemSegments = itemPath.split('/').filter(Boolean);
    const locationSegments = locationPath.split('/').filter(Boolean);

    return itemSegments.every((segment, index) => locationSegments[index] === segment);
  };

  const hasPermission = (item: layoutLib.SidebarItem, user: User) => {
    if (!item.requiredPermissions) return true;
    if (user.role === 'ADMIN') return true;

    return item.requiredPermissions.every((permission) => {
      if (typeof permission === 'string') {
        return Boolean(user.permissions?.[permission]);
      }

      const permissionForRole = permission[user.role];

      // если для текущей роли не задано требуемого перимешена -> доступ разрешен
      if (!permissionForRole) return true;

      return Boolean(user.permissions?.[permissionForRole]);
    });
  };

  const filterByAvailability = (
    menu: layoutLib.SidebarItem[]
  ): [layoutLib.SidebarItem[], string[], string[]] => {
    let cloneMenuItems = [...menu];
    let selectedKeys: string[] = [];
    let openKeys: string[] = [];
    // совпадения текущего уровня: выбираем только самое специфичное (по числу сегментов пути)
    const matchedItems: { key: string; depth: number }[] = [];

    cloneMenuItems.forEach((item) => {
      if (item.children && currentLocation.includes(item.path)) {
        openKeys.push(item.key.toString());
      }

      if (item.path && isActivePath(item.path, currentLocation)) {
        matchedItems.push({
          key: item.key.toString(),
          depth: item.path.split('/').filter(Boolean).length,
        });
      }

      if (!item.availableFor.includes(user?.role) || !hasPermission(item, user)) {
        cloneMenuItems = cloneMenuItems.filter((item2) => item.key !== item2.key);
        return cloneMenuItems;
      }

      if (item.children) {
        const nested = filterByAvailability(item.children);
        item.children = nested[0];
        selectedKeys = nested[1].length ? selectedKeys.concat(nested[1]) : selectedKeys;
        openKeys = nested[2].length ? openKeys.concat(nested[2]) : openKeys;
      }
    });

    if (matchedItems.length) {
      const maxDepth = Math.max(...matchedItems.map((match) => match.depth));
      selectedKeys = matchedItems
        .filter((match) => match.depth === maxDepth)
        .map((match) => match.key)
        .concat(selectedKeys);
    }

    return [cloneMenuItems, selectedKeys, openKeys];
  };

  const menuItems = () => {
    const sideBarData = layoutLib.getSidebarData(user, products);
    const isMultipleTeams = me && me?.teams?.length && me?.teams?.length > 1;

    if (isMultipleTeams) {
      const teamData = sideBarData[0]?.children?.find((item) => item.path === '/teams/');
      teamData!.label = 'Звена';
    }
    return filterByAvailability(sideBarData);
  };

  const [availableMenuItems, selectedKeys, openKeys] = menuItems();

  function getTeamId(user: User): string | undefined {
    if (!user.teams || user.teams.length === 0) return undefined;

    // Проверка: является ли пользователь тимлидом хотя бы в одной команде
    const leadTeam = user.teams.find(
      (team) => Array.isArray(team.teamLeads) && team.teamLeads.some((lead) => lead.id === user.id)
    );

    if (leadTeam) {
      return leadTeam.id.toString();
    }

    // Если не является тимлидом — возвращаем первую команду
    return user.teams[0]?.id.toString();
  }

  const providedPathVars = {
    TEAM_ID: getTeamId(user),
    USER_ID: user.id.toString(),
  };

  const menuItem = (mi: layoutLib.SidebarItem): ItemType => {
    const disabled = layoutLib.isNavLinkDisabled(providedPathVars, mi.providedPathVar);

    return {
      key: mi.key.toString(),
      title: '',
      label:
        mi.path && !mi.children
          ? renderNavLink({ to: layoutLib.getNavLinkPath(mi, providedPathVars), label: mi.label, disabled })
          : mi.label,
      icon: createElement(mi.icon),
      disabled,
      children: mi.children?.map(menuItem),
    };
  };

  const sidebarItems: MenuProps['items'] = availableMenuItems?.map(menuItem);

  return { items: sidebarItems, selectedKeys, openKeys };
};
